import { Container, Divider } from "@material-ui/core";
import { Add, Cached, LibraryBooks, Remove } from "@material-ui/icons";
import { Box, Grid } from "@mui/material";
import axios from "axios";
import {
  BalanceCard,
  CurrencyDisplay,
  ModalError,
  MonthYearRangePicker,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
  SkeletonComponent,
  StatusChip,
  TableAction,
} from "components";
import { ButtonGroupTop } from "components/atoms/Button";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { globalStyles } from "styles";
import { emptyText, getErrors } from "utils";
import { hardBaseUrl } from "../../../../services/urlConstant";
import ModalBalanceMutationDetail from "./DetailBalanceMutation";

const BalanceMutation = () => {
  const classes = globalStyles();
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [dataTableDetail, setDataTableDetail] = useState([]);

  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [queryParamsDetail, setQueryParamsDetail] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [tableTotalPageDetail, setTableTotalPageDetail] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [dataSummary, setDataSummary] = useState({});

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleChangeParamsDetail = (value, key) => {
    setQueryParamsDetail(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/balance/mutation`,
        {
          headers,
          params: {
            ...queryParams,
          },
        }
      );
      const { data, meta } = res?.data;
      setDataTable(data || []);

      const resBalance = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/balance`,
        {
          headers,
        }
      );
      setDataSummary({
        total_balance_composer: resBalance?.data.data.balance,
      });
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const getDataTableDetail = async (id, value, key) => {
    try {
      setLoadingPage(true);
      const res = await axios.get(
        `${hardBaseUrl}/composer/transaction/v2/balance/mutation/${id}`,
        {
          headers,
          params: {
            ...queryParamsDetail,
            [key]: value,
          },
        }
      );
      const { data, meta } = res?.data;
      setDataTableDetail(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
      handleChangeParamsDetail(id, "id");
      setShowModal(true);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Balance Mutation">
      <Container maxWidth={false}>
        <HeaderTitle title="Balance Mutation" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Box marginBottom="20px">
          <Divider />
        </Box>
        <Box mt="16px" mb="16px">
          <Grid container gap={2} justifyContent="flex-start">
            <BalanceCard
              title="Composer balance"
              value={dataSummary.total_balance_composer}
            />
          </Grid>
        </Box>
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.size}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            isHaveAction
            renderAction={item => (
              <TableAction
                handleView={() => getDataTableDetail(item.id)}
                // handleView={() => history.push(`/admin/sales-invoice`)}
              />
            )}
          />
        )}
        <ModalBalanceMutationDetail
          open={showModal}
          onClose={handleCloseModal}
          dataTable={dataTableDetail}
          totalPage={tableTotalPageDetail}
          queryParams={queryParamsDetail}
          getDataTableDetail={getDataTableDetail}
        />
      </Container>
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/advance",
  },
  {
    label: "Balance Mutation",
    active: true,
  },
];

const columnTable = [
  {
    name: "date",
    title: "Due Date",
  },
  {
    name: "description",
    title: "Description",
  },
  {
    name: "all",
    title: "Value",
    headerAlign: "right",

    renderText: item => {
      const colorReserve = {
        color: "#295ECC",
      };
      const colorIn = {
        color: "rgb(22 207 36)",
      };
      const colorOut = {
        color: "rgb(237 23 53)",
      };
      const unReserve = {
        color: "#rgb(237 161 23)",
      };
      return (
        <StatusChip
          startIcon={
            item.balance_tag === "reserved" ? (
              <Cached style={unReserve} />
            ) : item.balance_tag === "in" ? (
              <Add style={colorIn} />
            ) : item.balance_tag === "advance" ? (
              <Remove style={colorOut} />
            ) : (
              <Cached style={colorReserve} />
            )
          }
          label={
            <CurrencyDisplay value={item.value} decimalScale={2} prefix="Rp" />
          }
          customColorBackground="#F0F8FE"
          customColorText={
            item.balance_tag === "reserved"
              ? "rgb(237 161 23)"
              : item.balance_tag === "in"
              ? "rgb(22 207 36)"
              : item.balance_tag === "advance"
              ? "rgb(237 23 53)"
              : "#295ECC"
          }
          customBorder={
            item.balance_tag === "reserved"
              ? "rgb(237 161 23)"
              : item.balance_tag === "in"
              ? "rgb(22 207 36)"
              : item.balance_tag === "advance"
              ? "rgb(237 23 53)"
              : "#295ECC"
          }
          textAlign="right"
        />
      );
    },
  },
  {
    name: "balance",
    title: "Balance",
    headerAlign: "right",
    renderText: item => (
      <CurrencyDisplay value={item} decimalScale={2} prefix="Rp" />
    ),
  },
];
export default BalanceMutation;
